<div class="admissions-page">
  <h1 class="page-title">Admissions</h1>
  <p class="school-line" *ngIf="schoolName">
    <mat-icon aria-hidden="true">apartment</mat-icon>
    {{ schoolName }}
  </p>
  <p class="page-sub">
    End-to-end pipeline from first contact to enrolment: enquiries, applications, assessments, offers, and hand-off to student records and fees.
  </p>

  <div *ngIf="loading" class="loading-row">
    <mat-spinner diameter="40"></mat-spinner>
    <span>Loading admissions…</span>
  </div>

  <mat-tab-group *ngIf="!loading" animationDuration="0ms" class="adm-tabs">
    <mat-tab label="Overview">
      <div class="tab-body">
        <p class="tab-help">
          A strong ERP admissions module usually includes: enquiry capture, document checklist, entrance or interview scheduling, fee quotation / offer letter, and conversion tracking into active students (linked to Accounts).
        </p>
        <div class="stat-grid">
          <mat-card class="stat-card"><span class="n">{{ stats.enquiry }}</span><span class="lbl">Enquiries</span></mat-card>
          <mat-card class="stat-card"><span class="n">{{ stats.application }}</span><span class="lbl">Applications</span></mat-card>
          <mat-card class="stat-card"><span class="n">{{ stats.assessment }}</span><span class="lbl">Assessment</span></mat-card>
          <mat-card class="stat-card"><span class="n">{{ stats.offer }}</span><span class="lbl">Offers</span></mat-card>
          <mat-card class="stat-card"><span class="n">{{ stats.enrolled }}</span><span class="lbl">Enrolled</span></mat-card>
        </div>
        <mat-card class="hint-card">
          <mat-card-title>Suggested backend modules</mat-card-title>
          <mat-card-content>
            <ul class="hint-list">
              <li>CRM-style enquiry with source (walk-in, web, referral)</li>
              <li>Configurable admission year &amp; seat caps per class</li>
              <li>Document upload &amp; verification workflow</li>
              <li>Interview / entrance slots with notifications</li>
              <li>Offer letter + acceptance + first fee link to <strong>Accounts</strong></li>
              <li>One-click promote to <strong>Students</strong> with roll number rules</li>
            </ul>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>

    <mat-tab label="New enquiry">
      <div class="tab-body">
        <form [formGroup]="enquiryForm" (ngSubmit)="submitEnquiry()" class="enquiry-form">
          <div class="row2">
            <mat-form-field appearance="outline">
              <mat-label>Child first name</mat-label>
              <input matInput formControlName="childFirstName" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Child last name</mat-label>
              <input matInput formControlName="childLastName" />
            </mat-form-field>
          </div>
          <div class="row2">
            <mat-form-field appearance="outline">
              <mat-label>Class seeking</mat-label>
              <mat-select formControlName="seekingClass">
                <mat-option *ngFor="let c of classOptions" [value]="c">{{ c }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Academic year</mat-label>
              <input matInput formControlName="academicYear" placeholder="e.g. 2025-2026" />
            </mat-form-field>
          </div>
          <div class="row2">
            <mat-form-field appearance="outline">
              <mat-label>Parent / guardian name</mat-label>
              <input matInput formControlName="parentName" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Phone</mat-label>
              <input matInput formControlName="parentPhone" />
            </mat-form-field>
          </div>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Email (optional)</mat-label>
            <input matInput type="email" formControlName="parentEmail" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>Notes</mat-label>
            <textarea matInput formControlName="notes" rows="2"></textarea>
          </mat-form-field>
          <button mat-flat-button color="primary" type="submit" [disabled]="enquiryForm.invalid">Save enquiry</button>
        </form>
      </div>
    </mat-tab>

    <mat-tab label="Pipeline">
      <div class="tab-body">
        <p class="tab-help">All enquiries and applications. Admins can move a row to the next stage.</p>
        <table mat-table [dataSource]="enquiryData" class="pipe-table">
          <ng-container matColumnDef="createdAt">
            <th mat-header-cell *matHeaderCellDef>When</th>
            <td mat-cell *matCellDef="let row">{{ formatWhen(row.createdAt) }}</td>
          </ng-container>
          <ng-container matColumnDef="child">
            <th mat-header-cell *matHeaderCellDef>Child</th>
            <td mat-cell *matCellDef="let row">{{ row.childFirstName }} {{ row.childLastName }}</td>
          </ng-container>
          <ng-container matColumnDef="class">
            <th mat-header-cell *matHeaderCellDef>Class / year</th>
            <td mat-cell *matCellDef="let row">{{ row.seekingClass }} · {{ row.academicYear }}</td>
          </ng-container>
          <ng-container matColumnDef="parent">
            <th mat-header-cell *matHeaderCellDef>Parent</th>
            <td mat-cell *matCellDef="let row">{{ row.parentName }} · {{ row.parentPhone }}</td>
          </ng-container>
          <ng-container matColumnDef="stage">
            <th mat-header-cell *matHeaderCellDef>Stage</th>
            <td mat-cell *matCellDef="let row">
              <mat-form-field appearance="outline" class="dense-select" *ngIf="isAdmin">
                <mat-select [value]="row.stage" (selectionChange)="changeStage(row, $event.value)">
                  <mat-option *ngFor="let s of stages" [value]="s">{{ stageLabel(s) }}</mat-option>
                </mat-select>
              </mat-form-field>
              <span *ngIf="!isAdmin">{{ stageLabel(row.stage) }}</span>
            </td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let row">
              <a mat-button color="primary" routerLink="/home/students">Students</a>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="enquiryColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: enquiryColumns"></tr>
        </table>
        <p *ngIf="!enquiryData.data.length" class="empty">No enquiries yet. Add one under <strong>New enquiry</strong>.</p>
      </div>
    </mat-tab>

    <mat-tab label="Interviews &amp; visits">
      <div class="tab-body">
        <p class="tab-help">
          Schedule interviews or campus tours. When the API is ready, rows appear here from <code>GET /api/v1/admissions/interviews</code>.
        </p>
        <table mat-table [dataSource]="interviewData" class="pipe-table" *ngIf="interviewData.data.length">
          <ng-container matColumnDef="scheduledAt">
            <th mat-header-cell *matHeaderCellDef>When</th>
            <td mat-cell *matCellDef="let row">{{ formatWhen(row.scheduledAt) }}</td>
          </ng-container>
          <ng-container matColumnDef="enquiryId">
            <th mat-header-cell *matHeaderCellDef>Enquiry</th>
            <td mat-cell *matCellDef="let row">{{ row.enquiryId }}</td>
          </ng-container>
          <ng-container matColumnDef="mode">
            <th mat-header-cell *matHeaderCellDef>Mode</th>
            <td mat-cell *matCellDef="let row">{{ row.mode }}</td>
          </ng-container>
          <ng-container matColumnDef="outcome">
            <th mat-header-cell *matHeaderCellDef>Outcome</th>
            <td mat-cell *matCellDef="let row">{{ row.outcome || '—' }}</td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="interviewColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: interviewColumns"></tr>
        </table>
        <p *ngIf="!interviewData.data.length" class="empty">No interview slots recorded yet.</p>
      </div>
    </mat-tab>

    <mat-tab label="Documents &amp; fees">
      <div class="tab-body">
        <mat-card>
          <mat-card-title>Checklist (typical)</mat-card-title>
          <mat-card-content>
            <ul class="hint-list">
              <li>Birth certificate, previous school TC, address proof</li>
              <li>Medical / vaccination summary where required</li>
              <li>Admission fee + caution deposit → reconcile in <a routerLink="/home/accounts">Accounts</a></li>
            </ul>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
